import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { timeout } from "rxjs/operators";
import * as moment from "moment";
import { AppService } from "src/_services/app.service";
import {
  mtsDarkMode,
  setMtsDarkMode,
  mtsCanView,
} from "../mts-reporting-summary/mts-theme";

const REQUEST_TIMEOUT_MS = 30000;

// How many dots the trend line is allowed to draw. A dot per day is right for
// a month and unreadable for half a year, so a longer range plots every
// second, third or seventh day instead - which is what gives the weekly
// spacing on a six-month view.
const MAX_TREND_POINTS = 30;

// A ceiling on the days the window can expand to, so a malformed range cannot
// make the page build an unbounded array.
const MAX_TREND_DAYS = 400;


interface BetRow {
  betId: string;
  placedAt: number;
  sport: string;
  event: string;
  market: string;
  selection: string;
  betType: string;
  livePre: string;
  odds: number;
  stake: number;
  status: string;
  counted: string;
  pnl: number;
}

interface PointRow {
  day: number;
  date: string;
  pnl: number;
}

interface RejectionRow {
  code: number;
  reason: string;
  bets: number;
  sharePct: number;
}

interface SportRow {
  sportId: string;
  sportName: string;
  // "Exchange" or "SportsBook", and EMPTY unless this account used both.
  //
  // Sport ids are provider-scoped and the two spaces are disjoint - "4" and
  // "sr:sport:21" are both cricket - so an account that bet in both books has
  // two rows reading "Cricket". This is what tells them apart. The API blanks
  // it when there is only one book, because then every line would carry the
  // same word and it would separate nothing.
  book: string;
  bets: number;
  stakes: number;
  pnl: number;
  margin: number;
  avgOdds: number;
}

// The price profile. It comes from the bets, not from a reporting tier — the
// tiers carry money and counts and have never carried a bet's price.
//
// `bets` here is bets that CARRY a price, which is the population the averages
// are over, and it is deliberately not the same number as `totalBets`. The two
// are both shown so a gap between them reads as unpriced bets rather than as a
// figure that disagrees with the table above.
//
// No highest or lowest: these come from stored counters now, and a Redis hash
// only adds - an extreme cannot accumulate the way a sum and a count can.
interface OddsProfile {
  bets: number;
  average: number;
  winningBets: number;
  winningAverage: number;
  totalBets: number;
  liveBets: number;
  livePercent: number;
}

@Component({
  selector: "app-mts-account-detail",
  templateUrl: "./mts-account-detail.component.html",
  styleUrls: ["./mts-account-detail.component.scss"],
})
export class MtsAccountDetailComponent implements OnInit {
  canView = mtsCanView();
  checked = false;

  accountId = "";
  operatorId = "";
  partnerId = "";

  found = false;
  userName = "";
  currency = "";
  firstSeen = 0;
  lastSeen = 0;
  lifetime: any = null;
  lifetimeSingles: any = null;
  lifetimeMultiples: any = null;

  // The three cards the account page shows side by side. Built here rather
  // than written out three times in the template: the provider shows the same
  // measures in each, and three copies of a hundred lines of markup drift the
  // first time somebody adds a row to one of them.
  get lifetimeCards(): { title: string; data: any }[] {
    return [
      { title: "Total", data: this.lifetime },
      { title: "Singles", data: this.lifetimeSingles },
      { title: "Multiples", data: this.lifetimeMultiples },
    ];
  }

  // A getter hands *ngFor a new array of new objects on every change-detection
  // pass, and the default differ tracks by identity - so all three cards were
  // torn down and rebuilt on every tick. Track by the title, which is stable.
  trackCard(_: number, card: { title: string }): string {
    return card.title;
  }
  sportsTotal: any = null;
  sports: SportRow[] = [];
  series: PointRow[] = [];

  // The reference's two toggles. "Favourite sports" switches the share
  // between bet COUNT and TURNOVER; the P&L line switches its window.
  //
  // Singles / Multiples is NOT offered on these toggles. The split now exists
  // - the three lifetime cards show it - but a share-of-sport pie and a P&L
  // line are read over the whole account, and splitting them is its own
  // question rather than a second use of this control.
  // Account Info | Bet History, as the console has them. Two tabs because
  // they are two questions with two filters - putting one page's date range
  // above the other's table is what made it ambiguous which it scoped.
  tab: "info" | "bets" | "rejections" = "info";

  // One account's refusals, grouped by reason. Read from failed_bets rather
  // than the reporting tier: the tier knows how MANY were refused but not
  // why, because the reason is keyed beside the event and not beside the user.
  rejections: RejectionRow[] = [];
  rejectionsLoading = false;
  rejectionsError = "";
  rejectionsLoaded = false;

  // The bet history, paged on its own - it is a different question from the
  // figures above and a different read, so it has its own request and its own
  // pager rather than reloading the whole page.
  bets: BetRow[] = [];
  betsTotal = 0;
  betsCapped = false;
  betsPage = 1;
  betsPageSize = 20;
  betsLoading = false;
  betsError = "";
  private betsSeq = 0;

  insightMeasure: "bets" | "turnover" = "bets";
  odds: OddsProfile | null = null;
  // Set when the breakdown was DECLINED rather than empty - see SportsNote on
  // the response. Shown in place of both tables so "too much data" cannot read
  // as "this account did nothing".
  sportsNote = "";

  loading = false;
  // A range change on the account already on screen, as opposed to `loading`,
  // which is a cold load with nothing to show yet. The two are different
  // states and the page reacts to them differently: a cold load draws the
  // skeleton, a refresh leaves the figures up and marks the one section the
  // range actually scopes.
  refreshing = false;
  errorMessage = "";
  dark = mtsDarkMode();

  constructor(
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.canView = mtsCanView();
    this.checked = true;
    if (!this.canView) {
      return;
    }
    this.route.paramMap.subscribe((params) => {
      this.accountId = params.get("accountId") || "";
      const qp = this.route.snapshot.queryParamMap;
      // The operator travels in the URL because an account id is only unique
      // inside it. Without it this page would show whichever account the
      // database happened to return first.
      this.operatorId = qp.get("operatorId") || "";
      this.partnerId = qp.get("partnerId") || "";
      this.load();
    });
  }

  toggleTheme() {
    this.dark = !this.dark;
    setMtsDarkMode(this.dark);
    // Apex bakes its colours in at build time rather than reading CSS
    // variables, so a theme flip has to rebuild the options or the axes stay
    // the old theme's grey on the new theme's background.
    this.buildCharts();
  }

  back() {
    this.router.navigate(["/mts/accounts"]);
  }

  // This component is REUSED across accounts - the route is /mts/account/:id
  // and ngOnInit subscribes to paramMap - so both of these matter here in a
  // way they do not on a page that is built fresh each time.
  // No Timeframe dropdown.
  //
  // It offered "Overall" beside a date range, and the two were answering
  // different questions on the same control: the panels above are ALWAYS the
  // account's whole record, and the section below is bounded by the daily
  // tier's ninety-day retention - so "overall" there meant nothing a range
  // could not already say. One control, always a range.
  timeframe: "range" = "range";
  selected: any = {
    startDate: moment.utc().subtract(89, "days").startOf("day"),
    endDate: moment.utc().endOf("day"),
  };
  ranges: any = {
    "Last 7 days": [moment.utc().subtract(6, "days").startOf("day"), moment.utc().endOf("day")],
    "Last 30 days": [moment.utc().subtract(29, "days").startOf("day"), moment.utc().endOf("day")],
    "Last 90 days": [moment.utc().subtract(89, "days").startOf("day"), moment.utc().endOf("day")],
  };

  // The quick presets beside the picker. A number of days, because that is
  // what the reader is choosing - the calendar is there for the case the
  // presets do not cover.
  readonly presets = [
    { days: 7, label: "7D" },
    { days: 30, label: "30D" },
    { days: 90, label: "90D" },
  ];
  activePreset = 90;

  // THE REJECTIONS TAB HAS ITS OWN RANGE, and a narrower one.
  //
  // It is the only tab that reads failed_bets, and that read is capped at
  // MaxRejectionRangeDays on the API - one month. The other two tabs have no
  // such limit and default to ninety days, so sharing one control would mean
  // opening this tab on a range the server refuses, and the first thing a
  // reader saw would be an error they had done nothing to cause.
  //
  // The control that drives a query should carry that query's constraint
  // rather than inherit a different one.
  static readonly REJECTION_MAX_DAYS = 30;
  rejectionsSelected: any = {
    startDate: moment.utc().subtract(29, "days").startOf("day"),
    endDate: moment.utc().endOf("day"),
  };
  rejectionsRanges: any = {
    "Last 7 days": [moment.utc().subtract(6, "days").startOf("day"), moment.utc().endOf("day")],
    "Last 30 days": [moment.utc().subtract(29, "days").startOf("day"), moment.utc().endOf("day")],
  };
  readonly rejectionPresets = [
    { days: 7, label: "7D" },
    { days: 30, label: "30D" },
  ];
  rejectionsPreset = 30;

  setRejectionsPreset(days: number) {
    this.rejectionsPreset = days;
    this.rejectionsSelected = {
      startDate: moment.utc().subtract(days - 1, "days").startOf("day"),
      endDate: moment.utc().endOf("day"),
    };
    this.loadRejections();
  }

  // A hand-picked range is no longer any of the presets.
  onRejectionsRangePicked() {
    this.rejectionsPreset = 0;
  }

  // Only Account Info draws the presets, so this only ever runs with that tab
  // open - hence the straight load() rather than a branch on the tab.
  setPreset(days: number) {
    this.activePreset = days;
    this.selected = {
      startDate: moment.utc().subtract(days - 1, "days").startOf("day"),
      endDate: moment.utc().endOf("day"),
    };
    this.load(false);
  }

  // A hand-picked range is no longer any of the presets.
  onRangePicked() {
    this.activePreset = 0;
  }

  private loadSeq = 0;

  // A picked date is a CALENDAR date, not an instant - converting it to UTC
  // moves it back a day anywhere east of Greenwich. The presets are already
  // UTC moments and format the same either way.
  private payload(): any {
    const p: any = {
      accountId: this.accountId,
      operatorId: this.operatorId,
      partnerId: this.partnerId || undefined,
    };
    if (this.selected && this.selected.startDate) {
      p.fromDay = Number(moment(this.selected.startDate).format("YYYYMMDD"));
      p.toDay = Number(moment(this.selected.endDate).format("YYYYMMDD"));
    }
    return p;
  }


  apply() {
    // Same account, new range: a refresh, not a load.
    this.load(false);
  }

  // `reset` says this is a DIFFERENT account, not a new range on the one
  // already shown.
  //
  // It used to be unconditional, and that is what made every Apply blank the
  // whole page: the body renders on `found`, so setting it false tore down
  // the panels, both charts and the performance table, the skeleton took
  // their place, and everything faded back in a moment later - including the
  // account's own details and its lifetime figures, which do not move with a
  // range at all. Only the account identity changing needs that.
  load(reset: boolean = true) {
    if (!this.accountId || !this.operatorId) {
      this.errorMessage = "No account to show.";
      return;
    }
    const seq = ++this.loadSeq;
    this.errorMessage = "";

    if (reset) {
      // Clear the PREVIOUS account before fetching this one. The heading
      // updates at once, so without this, moving from account A to B showed
      // B's id over A's figures and A's charts, with no loading state on top
      // of them.
      this.loading = true;
      this.found = false;
      this.lifetime = null;
      this.lifetimeSingles = null;
      this.lifetimeMultiples = null;
      this.sports = [];
      this.sportsTotal = null;
      this.series = [];
      this.odds = null;
      this.sportsNote = "";
      this.sportsChart = null;
    } else {
      this.refreshing = true;
    }

    this.appService
      .getMtsAccountDetail(this.payload())
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.loadSeq) {
            return; // a newer account was opened; this reply is for the old one
          }
          this.loading = false;
          this.refreshing = false;
          if (!res || res.status !== "RS_OK") {
            this.errorMessage =
              (res && res.errorDescription) || "This account could not be loaded.";
            return;
          }
          this.found = !!res.found;
          this.userName = res.userName || "";
          this.currency = res.currency || "";
          this.firstSeen = res.firstSeen || 0;
          this.lastSeen = res.lastSeen || 0;
          this.lifetime = res.lifetime || null;
          this.lifetimeSingles = res.lifetimeSingles || null;
          this.lifetimeMultiples = res.lifetimeMultiples || null;
          this.sports = res.sports || [];
          this.sportsTotal = res.sportsTotal || null;
          this.series = res.series || [];
          this.betsPage = 1;
          if (this.tab === "bets") {
            this.loadBets();
          }
          this.odds = res.odds || null;
          this.sportsNote = res.sportsNote || "";
          this.buildCharts();
        },
        () => {
          if (seq !== this.loadSeq) {
            return;
          }
          this.loading = false;
          this.refreshing = false;
          this.errorMessage = "This account could not be loaded.";
        }
      );
  }



  stamp(ms: number): string {
    return ms ? moment.utc(ms).format("DD MMM YYYY, HH:mm") + " UTC" : "-";
  }

  // The calendar days the trend line covers: every day of the picked range,
  // in order, whether or not the account did anything on it.
  //
  // Bounded by the range the reader chose rather than by a count of its own,
  // so the one date control on the page decides what the chart shows and
  // there is nothing to contradict it. The daily tier only reaches back
  // ninety days, which is why the panel's caption says so - days before that
  // read as flat because there is no row, not because nothing happened.
  private windowDays(): string[] {
    const out: string[] = [];
    if (!this.selected || !this.selected.startDate || !this.selected.endDate) {
      return this.series.map((d) => d.date);
    }
    const cur = moment.utc(moment(this.selected.startDate).format("YYYY-MM-DD"));
    const end = moment.utc(moment(this.selected.endDate).format("YYYY-MM-DD"));
    // A guard, not a limit anyone should reach: a range is picked from a
    // calendar, so this only catches a malformed one.
    for (let i = 0; i <= MAX_TREND_DAYS && !cur.isAfter(end); i++) {
      out.push(cur.format("YYYY-MM-DD"));
      cur.add(1, "day");
    }
    return out;
  }

  // "YYYY-MM-DD" -> the UTC midnight that starts it, which is what the
  // datetime axis plots against. Parsed field by field rather than handed to
  // Date.parse: the day keys are UTC days, and a bare date string is only
  // guaranteed to be read as UTC in ES5 and later - this file targets es5
  // output but runs wherever the browser is.
  private dayStart(date: string): number {
    const [y, m, d] = (date || "").split("-").map((n) => Number(n));
    if (!y || !m || !d) {
      return NaN;
    }
    return Date.UTC(y, m - 1, d);
  }

  money(v: number): string {
    return (v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  pct(v: number): string {
    return `${(v || 0).toFixed(2)}%`;
  }

  sign(v: number): string {
    return v > 0 ? "pos" : v < 0 ? "neg" : "";
  }

  // The name a sport is shown under, in the table, the chart and the CSV.
  //
  // One function for all three: the book suffix only earns its place if it is
  // on every rendering of that row, and three copies of this is how one of
  // them ends up without it.
  sportLabel(s: SportRow): string {
    const name = s.sportName || s.sportId || "-";
    return s.book ? `${name} (${s.book})` : name;
  }

  downloadCsv() {
    const esc = (s: any) => `"${String(s == null ? "" : s).replace(/"/g, '""')}"`;
    const lines = [
      ["Sport", "No. of Bets", "Total Stakes", "Average Stake", "P&L", "Margin %", "Avg Odds"]
        .map(esc)
        .join(","),
    ];
    for (const sp of this.sports) {
      lines.push(
        [this.sportLabel(sp), sp.bets, this.money(sp.stakes),
          this.money(sp.bets ? sp.stakes / sp.bets : 0), this.money(sp.pnl),
          this.pct(sp.margin), sp.avgOdds ? sp.avgOdds.toFixed(2) : ""]
          .map(esc)
          .join(",")
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `mts-account-${this.accountId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // ---------------------------------------------------------------------
  // Charts
  //
  // Built from the per-sport rows the page already has, so it costs no
  // request. An empty list leaves the chart null and the template drops the
  // panel rather than rendering empty axes, which read as broken rather than
  // as no data.
  // ---------------------------------------------------------------------

  sportsChart: any = null;
  trendChart: any = null;

  // --- bet history ------------------------------------------------------

  loadBets() {
    if (!this.accountId || !this.operatorId) {
      return;
    }
    const seq = ++this.betsSeq;
    this.betsLoading = true;
    this.betsError = "";

    const p: any = this.payload();
    p.page = this.betsPage;
    p.pageSize = this.betsPageSize;

    this.appService
      .getMtsAccountBets(p)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.betsSeq) {
            return; // a newer page was asked for
          }
          this.betsLoading = false;
          if (!res || res.status !== "RS_OK") {
            this.bets = [];
            this.betsTotal = 0;
            this.betsError =
              (res && res.errorDescription) || "The bet history could not be loaded.";
            return;
          }
          this.bets = res.rows || [];
          this.betsTotal = res.total || 0;
          this.betsCapped = !!res.capped;
        },
        () => {
          if (seq !== this.betsSeq) {
            return;
          }
          this.betsLoading = false;
          this.bets = [];
          this.betsTotal = 0;
          this.betsError = "The bet history could not be loaded.";
        }
      );
  }

  get betsPages(): number {
    return Math.max(1, Math.ceil(this.betsTotal / this.betsPageSize));
  }

  goToBetsPage(p: number) {
    if (p < 1 || p > this.betsPages || p === this.betsPage) {
      return;
    }
    this.betsPage = p;
    this.loadBets();
  }

  // Shaded by how the REPORT counted the bet, never by whether the player
  // won. A badge coloured green for "Won" would be the one place on this page
  // where a colour meant the opposite of everywhere else - every other figure
  // here is the book's. The P&L column already carries that sign.
  //
  // An uncounted status - UNMATCHED, INITIATED, FLAGGED, OPEN - gets the plain
  // outline, which is the honest answer: the reporting path did not count it.
  countedClass(c: string): string {
    switch (c) {
      case "settled":
        return "is-settled";
      case "cashout":
        return "is-cashout";
      case "void":
        return "is-void";
      default:
        return "";
    }
  }

  // The bets tab's own Apply. It reloads only the list - the panels above it
  // are the account's whole record and do not move with a range.
  applyBets() {
    this.betsPage = 1;
    this.loadBets();
  }

  setTab(t: "info" | "bets" | "rejections") {
    if (this.tab === t) {
      return;
    }
    this.tab = t;
    // The bet history is only fetched when it is first looked at - most visits
    // never open it, and it is the one read that goes to the bets collection.
    if (t === "bets" && !this.bets.length && !this.betsLoading) {
      this.loadBets();
    }
    // Fetched on first open, like the bet history - and guarded on a flag
    // rather than on the row count, because an account with no refusals at
    // all is a real and common answer that would otherwise re-ask forever.
    if (t === "rejections" && !this.rejectionsLoaded && !this.rejectionsLoading) {
      this.loadRejections();
    }
  }

  loadRejections() {
    if (!this.accountId || !this.operatorId) {
      return;
    }
    const seq = ++this.rejectionsSeq;
    this.rejectionsLoading = true;
    this.rejectionsError = "";

    this.appService
      .getMtsAccountRejections(this.rejectionsPayload())
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
        (res) => {
          if (seq !== this.rejectionsSeq) {
            return; // a newer range was asked for
          }
          this.rejectionsLoading = false;
          this.rejectionsLoaded = true;
          if (!res || res.status !== "RS_OK") {
            this.rejections = [];
            this.rejectionsError =
              (res && res.errorDescription) || "The rejections could not be loaded.";
            return;
          }
          this.rejections = res.rows || [];
        },
        () => {
          if (seq !== this.rejectionsSeq) {
            return;
          }
          this.rejectionsLoading = false;
          this.rejectionsLoaded = true;
          this.rejections = [];
          this.rejectionsError = "The rejections could not be loaded.";
        }
      );
  }

  private rejectionsSeq = 0;

  // The same identity as every other call on this page, but this tab's own
  // days - see rejectionsSelected.
  private rejectionsPayload(): any {
    return {
      accountId: this.accountId,
      operatorId: this.operatorId,
      partnerId: this.partnerId || undefined,
      fromDay: Number(moment(this.rejectionsSelected.startDate).format("YYYYMMDD")),
      toDay: Number(moment(this.rejectionsSelected.endDate).format("YYYYMMDD")),
    };
  }

  // The range control is shared, so applying it must re-ask for whichever tab
  // is open - otherwise the heading says one range and the table shows another.
  applyRejections() {
    this.loadRejections();
  }

  setInsightMeasure(m: "bets" | "turnover") {
    if (this.insightMeasure === m) {
      return;
    }
    this.insightMeasure = m;
    this.buildCharts();
  }


  // Chart colours, kept beside the page's own tokens rather than read from
  // them: Apex bakes its palette in at build time and cannot resolve a CSS
  // variable, which is why a theme flip rebuilds the options instead.
  //
  // The grid is deliberately far lighter than a border - it is there to be
  // measured against, not looked at, and a grid that competes with the series
  // is the single most common way a chart stops being readable.
  private chartTheme() {
    return this.dark
      ? {
          fg: "#94a3b8",
          grid: "#1f2a37",
          bar: "#60a5fa",
          up: "#34d399",
          down: "#f87171",
        }
      : {
          fg: "#6b7280",
          grid: "#eef1f5",
          bar: "#2563eb",
          up: "#059669",
          down: "#dc2626",
        };
  }

  buildCharts() {
    const t = this.chartTheme();

    // --- Favourite sports -------------------------------------------------
    //
    // A SHARE, as the reference draws it: each sport's percentage of the
    // whole, not its raw figure. That is what makes one bar comparable to the
    // next without reading the axis, and it is why the axis runs 0-100%.
    const totalFor = (sp: SportRow) =>
      this.insightMeasure === "bets" ? sp.bets : sp.stakes;
    const grand = this.sports.reduce((a, sp) => a + (totalFor(sp) || 0), 0);
    const ranked = this.sports
      .filter((sp) => totalFor(sp) > 0)
      .sort((a, b) => totalFor(b) - totalFor(a))
      .slice(0, 8);

    if (ranked.length && grand > 0) {
      this.sportsChart = {
        series: [
          {
            name: this.insightMeasure === "bets" ? "Share of bets" : "Share of turnover",
            data: ranked.map((sp) => Number(((100 * totalFor(sp)) / grand).toFixed(2))),
          },
        ],
        chart: {
          type: "bar",
          height: Math.max(150, 46 * ranked.length),
          toolbar: { show: false },
          foreColor: t.fg,
          background: "transparent",
          animations: { enabled: false },
          fontFamily: "inherit",
        },
        colors: [t.bar],
        // No track behind the bars. One was tried, on the reasoning that a 2%
        // share needs something to be a share OF - but six full-width grey
        // tubes with a short fill in each read as a panel that had not
        // finished loading, and the reference does not have them either. The
        // percentage is on every label, which is what actually carries the
        // small values.
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4, barHeight: "38%" },
        },
        // The label rides ABOVE its own bar, as the reference has it - so a
        // short bar is still named without a legend to look across to.
        dataLabels: {
          enabled: true,
          offsetX: 0,
          offsetY: -20,
          textAnchor: "start",
          style: { fontSize: "11px", fontWeight: 600, colors: [t.fg] },
          formatter: (v: number, o: any) =>
            `${v.toFixed(2)}%  ${this.sportLabel(ranked[o.dataPointIndex])}`,
        },
        // No grid at all: the labels sit on the bars and the track shows the
        // whole, so gridlines would be furniture with nothing to measure.
        grid: { show: false, padding: { top: 6, right: 8, bottom: 0, left: 8 } },
        xaxis: {
          max: 100,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { show: false } },
        tooltip: {
          theme: this.dark ? "dark" : "light",
          y: {
            formatter: (v: number, o: any) => {
              const sp = ranked[o.dataPointIndex];
              const raw =
                this.insightMeasure === "bets"
                  ? `${sp.bets.toLocaleString()} bets`
                  : this.money(sp.stakes);
              return `${v.toFixed(2)}%  (${raw})`;
            },
          },
        },
      };
    } else {
      this.sportsChart = null;
    }

    // --- Betting visual insight ------------------------------------------
    //
    // CUMULATIVE, as the reference draws it: the line is where the account
    // stands, not what it did on one day. A per-day line of small swings says
    // nothing about whether the book is ahead.
    // EVERY DAY IN THE WINDOW, not only the days that have a row.
    //
    // this.series carries one entry per day the account actually did
    // something, so plotting it directly drew a line through whatever days
    // happened to exist and labelled it "the last 90 days" - an account
    // active on five days in three months got a five-point chart. A quiet day
    // is a real fact about the account: the book's position did not move. It
    // belongs on the line, flat, at the figure it was already at.
    const byDay: { [day: string]: number } = {};
    for (const d of this.series) {
      byDay[d.date] = (byDay[d.date] || 0) + (d.pnl || 0);
    }
    const dense = this.windowDays().map((date) => ({ date, pnl: byDay[date] || 0 }));

    if (dense.length) {
      let running = 0;
      // {x, y} against a DATETIME axis, not a bare list against a category
      // one. A category axis gives every day its own tick and honours
      // tickAmount only as a hint, so the labels came out printed over each
      // other - "2026-07-182026-07-22" - and no amount of rotating or hiding
      // fixed it. On a datetime axis Apex picks the intervals itself and the
      // labels never collide, however long the range is.
      const daily = dense.map((d) => {
        running += d.pnl;
        return { x: this.dayStart(d.date), y: Number(running.toFixed(2)) };
      });
      // A dot on every plotted point, as the reference has it - which means
      // the number of PLOTTED points has to stay somewhere a reader can count.
      // Sampling a CUMULATIVE line is safe in a way sampling a daily one is
      // not: each point is still the exact position on its own date, so the
      // only thing dropped is movement between two dates, never a total.
      const step = Math.max(1, Math.ceil(daily.length / MAX_TREND_POINTS));
      const points = [];
      for (let i = 0; i < daily.length; i += step) {
        points.push(daily[i]);
      }
      // The last day always, whatever the step lands on - it is where the
      // account stands now, which is the one point nobody should have to
      // interpolate.
      if (points[points.length - 1] !== daily[daily.length - 1]) {
        points.push(daily[daily.length - 1]);
      }
      this.trendChart = {
        series: [{ name: "Cumulative P&L", data: points }],
        chart: {
          type: "area",
          height: 260,
          toolbar: { show: false },
          zoom: { enabled: false },
          foreColor: t.fg,
          background: "transparent",
          animations: { enabled: false },
          fontFamily: "inherit",
        },
        colors: [t.bar],
        dataLabels: { enabled: false },
        // STRAIGHT, not smoothed. A smoothed cumulative line bulges past the
        // points either side of a move, so it draws the position reaching
        // figures the account never held - and on a flat run it wobbles where
        // nothing happened at all.
        stroke: { curve: "straight", width: 2.5, lineCap: "round" },
        // A dot on EVERY plotted point, which is what the reference draws and
        // what makes a flat stretch legible: without them a run of quiet days
        // is an unbroken line that says nothing about how many days it spans.
        // The sampling above is what keeps them countable.
        markers: {
          size: 4,
          strokeWidth: 2,
          strokeColors: "transparent",
          hover: { size: 6 },
        },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 0.4, opacityFrom: 0.22, opacityTo: 0, stops: [0, 95] },
        },
        // Horizontal lines only. Vertical ones on a dated axis imply a
        // boundary between days that the measure does not have.
        grid: {
          borderColor: t.grid,
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          padding: { left: 4, right: 12, top: 0 },
        },
        xaxis: {
          type: "datetime",
          axisBorder: { show: false },
          axisTicks: { show: false },
          // datetimeUTC, because the day keys ARE UTC days. Left to the
          // browser's zone, a reader west of Greenwich sees every point
          // labelled with the day before the one it belongs to.
          // dd/MM/yyyy, as the reference labels them. The year is worth its
          // width here: a range can span one, and "05/10" against "05/10"
          // twelve months apart is the kind of ambiguity a money figure
          // should never be read through.
          labels: { rotate: 0, datetimeUTC: true, format: "dd/MM/yyyy" },
          tickAmount: Math.min(12, points.length),
        },
        yaxis: {
          labels: { formatter: (v: number) => this.money(v) },
          tickAmount: 4,
        },
        // Zero is the only threshold on this axis that means anything: above
        // it the book is ahead, below it behind. Drawn in the label colour
        // rather than the grid colour so it reads as a reference line and not
        // as one more gridline.
        annotations: {
          yaxis: [{ y: 0, borderColor: t.fg, strokeDashArray: 3, opacity: 0.35 }],
        },
        tooltip: {
          theme: this.dark ? "dark" : "light",
          x: { format: "dd/MM/yyyy" },
          y: { formatter: (v: number) => this.money(v) },
        },
      };
    } else {
      this.trendChart = null;
    }
  }

}
